import { Button, Stack, Text } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { TextInput } from '@components';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { useForgotPassword } from '@hooks/user';
import { AuthLayout } from './AuthLayout';

const forgotSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' }).min(1, { message: 'Email required' }),
});

type ForgotFormValues = z.infer<typeof forgotSchema>;

export const ForgotPassword = () => {
  const [sent, setSent] = useState(false);

  const forgotPasswordMutation = useForgotPassword(() => setSent(true));

  const form = useFormWithNameInputProp<ForgotFormValues>({
    initialValues: { email: '' },
    validate: zodResolver(forgotSchema),
    validateInputOnBlur: true,
  });

  const onSubmit = (values) => forgotPasswordMutation.mutate(values);

  return (
    <AuthLayout title="Forgot password">
      <form onSubmit={form.onSubmit(onSubmit)}>
        {sent && <Text>A password reset link has been sent to your email address.</Text>}

        {!sent && (
          <Stack gap="md">
            <TextInput {...form.getInputProps('email')} data-pw="login-email-input" label="Email" size="lg" />
            {forgotPasswordMutation.isError && (
              <Text c="red" fw={700} data-pw="login-user-error">
                {forgotPasswordMutation.error.response?.data?.message ?? 'Forgot password failed'}
              </Text>
            )}
            <Button data-pw="login-submit-btn" type="submit" variant="filled" size="lg">
              Forgot password
            </Button>
          </Stack>
        )}
      </form>
      <Text ta="right" mt="md">
        <Link to={'/auth'}>← Back to login</Link>
      </Text>
    </AuthLayout>
  );
};
